import { useEffect, useMemo, useState } from 'react'
import useMsiResearchDataset from '../../hooks/useMsiResearchDataset'
import exportMsiResearchDataXlsx from '../../helpers/analysisHelpers/exportMsiResearchDataXlsx'

// Ongefilterd kan de tabel respondenten × alle items van de hele meetstandaard
// worden — alles tegelijk renderen bevriest de pagina. Daarom pagina's van
// deze grootte; de download bevat altijd alle rijen.
const ROWS_PER_PAGE = 200

/**
 * Onderliggende data van de meetstandaard-onderzoekspagina (superTenant),
 * achter een doorklik. Te downloaden als xlsx: precies de zichtbare kolommen
 * en ALLE rijen die bij het filter horen, niet alleen de huidige pagina
 * (besluit user 2026-10-12 — verving 'bekijken mag, downloaden niet').
 *
 * Respondenten zijn gepseudonimiseerd (R1…Rn, stabiel binnen de dataset);
 * organisatienamen zijn zichtbaar — organisaties zijn geen personen en de
 * onderzoekers zijn Deccos-staf.
 *
 * Vanuit de analyse is een datapunt aanklikbaar: een effect-rij (alpha)
 * filtert op dat effect, een item-rij (factoranalyse) op dat ene item.
 *
 * @param {object} props
 * @param {string} props.sector - Sector-slug uit de querystring; '' = hele meetstandaard
 * @param {string} [props.effectFilter] - msiId — toon alleen de items van dit effect
 * @param {string} [props.itemFilter] - Kolomsleutel — toon alleen dit item
 * @param {() => void} [props.onClearFilter] - Filter wissen (terug naar de volledige dataset)
 */
const MsiResearchDataTable = ({ sector, effectFilter = '', itemFilter = '', onClearFilter }) => {
    const { dataset, loading, error } = useMsiResearchDataset(sector)
    const [page, setPage] = useState(0)

    // Ander filter of andere sector = andere rijen → terug naar pagina 1
    useEffect(() => {
        setPage(0)
    }, [sector, effectFilter, itemFilter])

    const visibleColumns = useMemo(() => {
        if (!dataset) return []
        if (itemFilter) return dataset.columns.filter((column) => column.key === itemFilter)
        if (effectFilter) return dataset.columns.filter((column) => column.msiId === effectFilter)
        return dataset.columns
    }, [dataset, effectFilter, itemFilter])

    // Gefilterd: alleen respondent-rijen met minstens één score op de zichtbare items
    const visibleRows = useMemo(() => {
        if (!dataset) return []
        return dataset.rows.filter((row) =>
            visibleColumns.some((column) => Number.isFinite(row.scores[column.key]))
        )
    }, [dataset, visibleColumns])

    if (error) return <p className='deep-analysis-error'>{error}</p>
    if (loading) return <p>Onderliggende data wordt geladen…</p>

    if (!dataset || dataset.rows.length === 0) {
        return (
            <div className='deep-analysis-section'>
                <p className='deep-analysis-empty'>Geen data gevonden.</p>
            </div>
        )
    }

    const pageCount = Math.max(1, Math.ceil(visibleRows.length / ROWS_PER_PAGE))
    const currentPage = Math.min(page, pageCount - 1)
    const pageStart = currentPage * ROWS_PER_PAGE
    const pageRows = visibleRows.slice(pageStart, pageStart + ROWS_PER_PAGE)

    const download = () => exportMsiResearchDataXlsx(
        { columns: visibleColumns, rows: visibleRows },
        `meetstandaard-data${sector ? `-${sector}` : ''}.xlsx`
    )

    const filterLabel = itemFilter
        ? visibleColumns[0]?.itemTitle || 'onbekend item'
        : effectFilter
            ? visibleColumns[0]?.effectName || 'onbekend effect'
            : ''

    return (
        <div className='deep-analysis-section'>
            <div className='deep-analysis-section-header'>
                <h3>Onderliggende data</h3>
                <p>
                    Eén rij per respondent per organisatie per meetmoment; de score per
                    item is het (gespiegelde) gemiddelde van de ingevulde antwoorden.
                    Respondenten zijn gepseudonimiseerd (R1…Rn).
                </p>
            </div>
            {filterLabel && (
                <p className='msi-research-data-filter'>
                    Gefilterd op: <strong>{filterLabel}</strong>
                    {onClearFilter && (
                        <span className='msi-research-data-filter-clear' onClick={onClearFilter}>
                            Toon alles
                        </span>
                    )}
                </p>
            )}
            {visibleColumns.length === 0 && (
                <p className='deep-analysis-empty'>Dit datapunt is niet (meer) in de dataset gevonden.</p>
            )}
            {visibleColumns.length > 0 && (
                <div className='msi-research-data-actions'>
                    <button type='button' className='msi-research-data-download' onClick={download}>
                        Download data (xlsx) ↓
                    </button>
                </div>
            )}
            {visibleColumns.length > 0 && (
                <div className='msi-research-data-table-wrapper'>
                    <table className='msi-research-data-table'>
                        <thead>
                            <tr>
                                <th>Respondent</th>
                                <th>Organisatie</th>
                                <th>Meetmoment</th>
                                {visibleColumns.map((column) => (
                                    <th key={column.key} title={column.effectName}>
                                        {column.itemTitle}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.respondentLabel}</td>
                                    <td>{row.orgName}</td>
                                    <td>{row.momentLabel}</td>
                                    {visibleColumns.map((column) => (
                                        <td key={column.key}>
                                            {Number.isFinite(row.scores[column.key])
                                                ? row.scores[column.key].toFixed(1)
                                                : '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {pageCount > 1 && (
                        <div className='msi-research-data-pager'>
                            <button
                                type='button'
                                disabled={currentPage === 0}
                                onClick={() => setPage(currentPage - 1)}
                            >
                                Vorige
                            </button>
                            <p className='msi-research-data-rowcap'>
                                Rijen {pageStart + 1}–{pageStart + pageRows.length} van {visibleRows.length}
                            </p>
                            <button
                                type='button'
                                disabled={currentPage === pageCount - 1}
                                onClick={() => setPage(currentPage + 1)}
                            >
                                Volgende
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default MsiResearchDataTable
